import {
    AuthContext,
    ClaimSearchResult, ClaimType,
    GuarantorSearchResult,
    PatientSearchResult,
    PayerSearchResult,
    ProviderClaim,
    ProviderPatient,
    ProviderPayer,
    ProviderProvider,
    ProviderSearchResult,
    RecentSearchResult,
    SearchBar,
} from '@enter/core';
import { mdiCloseCircle, mdiMagnify } from '@mdi/js';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ClaimSearchMode, handleError, IInjectedRootStore, ISearchResult } from '../../stores';
import * as S from './claim-search-bar.styles';
import {ClaimTypeTitle} from "./claim-search-bar.styles";

interface IClaimSearchBarState {
    open: boolean;
    searching: boolean;
    selectedItem?: string;
    recentSearches: string[];
    searchResult: ISearchResult | undefined;
    searchTerm: string;
    searchMode: ClaimSearchMode;
}

const maxRecentSearches = 9;
const debouncePeriod = 800;
const searchModes: ClaimSearchMode[] = ['patient', 'guarantor', 'provider', 'payer'];

type Props = {
    onChange?: () => void;
};

@inject('rootStore')
@observer
export class ClaimSearchBar extends React.Component<Partial<Props>, IClaimSearchBarState> {
    public static contextType = AuthContext;
    private static saveRecentSearches = (recentSearches: string[]): void => {
        window.localStorage.setItem('search_recent', JSON.stringify(recentSearches));
    };
    private static loadRecentSearches = (): string[] => {
        const recent = window.localStorage.getItem('search_recent');
        if (!_.isNil(recent)) {
            return JSON.parse(recent);
        }
        return [];
    };
    private readonly debouncedSearch: _.DebouncedFunc<(mode: ClaimSearchMode, term: string, claimType: ClaimType) => Promise<void>>;

    constructor(props: Props) {
        super(props);
        this.state = {
            open: false,
            searching: false,
            selectedItem: undefined,
            recentSearches: ClaimSearchBar.loadRecentSearches(),
            searchResult: undefined,
            searchTerm: '',
            searchMode: 'patient',
        };
        this.debouncedSearch = _.debounce(this.doSearch, debouncePeriod);
    }
    public componentDidMount(): void {
        window.addEventListener('keydown', this.handleKeyDown);
    }
    public componentWillUnmount(): void {
        window.removeEventListener('keydown', this.handleKeyDown);
    }
    public render(): React.ReactNode {
        return (
            <SearchBar
                open={this.state.open}
                text={this.state.searchTerm}
                placeholder="Search"
                controls={this.renderControls()}
                recent={this.renderRecent()}
                noResults={this.renderNoResults()}
                results={this.renderResults()}
                onChange={this.handleChange}
                onFocus={this.handleFocus}
                loading={this.state.searching}
                placement="bottom"
            />
        );
    }
    private get injected(): Props & IInjectedRootStore {
        return this.props as Props & IInjectedRootStore;
    }

    private readonly findClaim = (claimId: string): ProviderClaim | undefined => {
        const { searchResult } = this.state;
        if (searchResult) {
            return _.find(searchResult.claims, { id: claimId });
        }
    };

    private readonly findPayer = (payerId: string): ProviderPayer | undefined => {
        const { searchResult } = this.state;
        if (searchResult) {
            return _.find(searchResult.payers, { id: payerId });
        }
    };

    private readonly findProvider = (providerId: string): ProviderProvider | undefined => {
        const { searchResult } = this.state;
        if (searchResult) {
            return _.find(searchResult.providers, { id: providerId });
        }
    };

    private readonly renderControls = (): React.ReactNode => {
        return <S.Controls key="modes">
            <S.Header>
                    <S.HeaderText>Search Results</S.HeaderText>
                    <S.Spacer />
                     <S.CloseButton onClick={this.doClose}>
                     <S.CloseIcon path={mdiCloseCircle} size={1} />
                    </S.CloseButton>
            </S.Header>
        </S.Controls>;
        // const { metricsStore } = this.props.rootStore;
        // const claimTypes: ClaimType[] = this.props.rootStore?.metricsStore.selectedClaimTypes ? this.props.rootStore?.metricsStore.selectedClaimTypes : [ClaimType.PROFESSIONAL];
        // const claimType: ClaimType = (claimTypes.indexOf(ClaimType.INSTITUTIONAL) > -1) ? ClaimType.INSTITUTIONAL : ClaimType.PROFESSIONAL;
        //
        // const buttons = _.map(searchModes, mode => (
        //     <S.ModeButton
        //         key={mode}
        //         size="small"
        //         variant="outlined"
        //         active={this.state.searchMode === mode}
        //         onClick={this.handleModeClick(mode)}>
        //         {mode}
        //     </S.ModeButton>
        // ));
        // return (
        //     <S.Controls key="modes">
        //         <S.ModeTitle>Search mode<S.ClaimTypeTitle>{claimType === ClaimType.PROFESSIONAL ? 'Professional' : 'Institutional'}</S.ClaimTypeTitle></S.ModeTitle>
        //         {buttons}
        //         <S.Spacer />
        //         <S.CloseButton onClick={this.doClose}>
        //             <S.CloseIcon path={mdiCloseCircle} size={1} />
        //         </S.CloseButton>
        //     </S.Controls>
        // );
    };
    private readonly renderRecent = (): React.ReactNode | undefined => {
        return (
            <>
                <RecentSearchResult
                    onClick={this.handleRecentClick}
                    onDelete={this.handleRecentDelete}
                    selected={this.state.selectedItem}
                    terms={this.state.recentSearches}
                />
            </>
        );
    };
    private readonly renderNoResults = (): React.ReactElement[] => {
        return [
            <S.NoResults key="no-results">
                <S.NoResultsIcon path={mdiMagnify} size={1} />
                <S.NoResultsText>Your search criteria did not return any matches.</S.NoResultsText>
            </S.NoResults>,
        ];
    };
    private readonly renderResults = (): React.ReactElement[] | undefined => {
        let results: React.ReactElement[] | undefined;
        const { searchMode, searchTerm } = this.state;
        if (searchTerm) {
            if (searchMode === 'patient') {
                results = this.renderPatientResults();
            } else if (searchMode === 'guarantor') {
                results = this.renderGuarantorResults();
            } else if (searchMode === 'payer') {
                results = this.renderPayerResults();
            } else if (searchMode === 'provider') {
                results = this.renderProviderResults();
            }
        }
        return _.isEmpty(results) ? undefined : results;
    };
    private readonly renderClaimResult = (claim: ProviderClaim): React.ReactElement => {
        return (
            <ClaimSearchResult
                key={claim.id}
                id={claim.id}
                externalId={claim.accountNumber}
                fromDos={claim.fromDos}
                throughDos={claim.throughDos}
                primaryPayerName={claim.primaryPayerName}
                claimResponsibilityType={claim.claimResponsibilityType}
                charge={_.get(claim, 'chargeAmount.amount', '0')}
                selected={claim.id === this.state.selectedItem}
                onClick={this.handleClaimClick(claim)}
            />
        );
    };

    private readonly renderClaimResults = (claimIds: string[]): React.ReactNode => {
        const foundClaims = _.compact(_.map(claimIds, claimId => this.findClaim(claimId)));
        const claimSearchResults = _.map(foundClaims, claim => this.renderClaimResult(claim));
        return (
            <>
                <S.ClaimsRow>
                    <S.ClaimsHeader />
                    <S.ClaimsHeader>Date</S.ClaimsHeader>
                    <S.ClaimsHeader>Id</S.ClaimsHeader>
                    <S.ClaimsHeader>Status</S.ClaimsHeader>
                    <S.ClaimsHeader>Payer</S.ClaimsHeader>
                    <S.ClaimsHeader>Charge</S.ClaimsHeader>
                </S.ClaimsRow>
                {claimSearchResults}
            </>
        );
    };

    private readonly renderPatientResults = (): React.ReactElement[] => {
        const { searchResult } = this.state;
        const map = this.buildClaimsMap(searchResult);
        const results = _.map(map, (claimIds: string[], patientKey: string) => {
            if (searchResult) {
                try {
                    const claim = this.findClaim(claimIds[0]);
                    const id = (claim?.patientId) || '';
                    const first = (claim?.patientFirst) || '';
                    const last = (claim?.patientLast) || '';
                    const suffix = (claim?.patientSuffix) || '';
                    const externalId = (claim?.patientExternalId) || '';
                    const sex = '';
                    const dob = claim?.['patientDob'] || new Date(0).toISOString();
                    const nickname = (claim?.patientNickname) || '';
                    return (
                        <PatientSearchResult
                            key={patientKey}
                            id={id}
                            externalId={externalId}
                            first={first}
                            last={last}
                            suffix={suffix}
                            sex={sex}
                            nickname={nickname}
                            dob={dob}>
                            {this.renderClaimResults(claimIds)}
                        </PatientSearchResult>
                    );
                } catch (err) {
                    // NB: protect against un-renderable patients
                    // tslint:disable-next-line: no-console
                    console.error(err);
                    return <></>;
                }
            }
        });
        return _.compact(results);
    };
    private readonly renderGuarantorResults = (): React.ReactElement[] => {
        const { searchResult } = this.state;
        const map = this.buildClaimsMap(searchResult);
        const results = _.map(map, (claimIds: string[], key: string) => {
            if (searchResult) {
                try {
                    const guarantor = _.find(searchResult.guarantors, { id: key });
                    if (guarantor) {
                        return (
                            <GuarantorSearchResult
                                key={key}
                                id={guarantor.id}
                                first={guarantor.first}
                                last={guarantor.last}
                                email={guarantor.email}
                                dob={guarantor.dob}>
                                {this.renderClaimResults(claimIds)}
                            </GuarantorSearchResult>
                        );
                    }
                } catch (err) {
                    // NB: protect against un-renderable guarantors
                    // tslint:disable-next-line: no-console
                    console.error(err);
                    return <></>;
                }
            }
        });
        return _.compact(results);
    };
    private readonly renderPayerResults = (): React.ReactElement[] => {
        const payers = this.state.searchResult ? this.state.searchResult.payers : [];
        const results = _.map(payers, payer => (
            <PayerSearchResult
                key={payer.id}
                id={payer.id}
                name={payer.name}
                phone={payer.contact?.phoneClaim}
                email={payer.contact?.emailClaim}
                selected={payer.id === this.state.selectedItem}
            />
        ));
        return _.compact(results);
    };
    private readonly renderProviderResults = (): React.ReactElement[] => {
        const providers = this.state.searchResult ? this.state.searchResult.providers : [];
        const results = _.map(providers, provider => (
            <ProviderSearchResult
                key={provider.id}
                id={provider.id}
                first={provider.first}
                suffix={provider.suffix}
                last={provider.last}
                email={provider.email}
                npi={provider.npi}
                type={provider.type}
                selected={provider.id === this.state.selectedItem}
            />
        ));
        return _.compact(results);
    };
    private readonly handleModeClick = (mode: ClaimSearchMode): (() => void) => () => {
        if (mode) {
            this.setState(
                {
                    searchMode: mode,
                },
                () => this.beginSearch(),
            );
        }
    };

    private readonly handleClaimClick = (claim: ProviderClaim): (() => void) => {
        return (): void => {
            this.activateClaim(claim.id);
        };
    };
    private readonly handleRecentClick = (term: string): void => {
        this.activateRecent(term);
    };
    private readonly handleRecentDelete = (term: string): void => {
        const recentSearches = _.without(this.state.recentSearches, term);
        this.setState({ recentSearches });
        ClaimSearchBar.saveRecentSearches(recentSearches);
    };
    private readonly handleKeyDown = (e: KeyboardEvent): void => {
        const isBodyTarget = e.target === document.body;
        const selectedItem = this.state.selectedItem as string;
        if (isBodyTarget) {
            // NB: only consider printable chars if events are handled by 'body'
            if (e.code === 'Slash') {
                if (!this.state.open) {
                    this.setState({ open: true });
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        } else if (e.code === 'Escape') {
            this.doClose();
        } else if (selectedItem && e.code === 'Enter') {
            this.activateItem(selectedItem, e.shiftKey);
        } else if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
            this.setState({
                selectedItem: this.selectNextItem(e.code === 'ArrowUp'),
            });
        } else if (e.shiftKey && (e.code === 'ArrowLeft' || e.code === 'ArrowRight')) {
            const i = _.indexOf(searchModes, this.state.searchMode);
            const delta = e.code === 'ArrowRight' ? 1 : -1;
            const mode = searchModes[(i + searchModes.length + delta) % searchModes.length];
            this.handleModeClick(mode)();
        } else if (e.code === 'Delete' && e.shiftKey) {
            const recentSearches = _.without(this.state.recentSearches, selectedItem);
            this.setState({ recentSearches });
            ClaimSearchBar.saveRecentSearches(recentSearches);
        }
    };
    private readonly handleChange = async (searchTerm: string | undefined): Promise<void> => {
        if (!!searchTerm) {
            this.beginSearch(searchTerm);
        } else {
            this.setState({ searchTerm: '' });
        }
    };
    private readonly handleFocus = (): void => {
        if (!this.state.open) {
            this.setState({
                open: true,
            });
        }
    };
    private readonly beginSearch = (searchTerm: string = this.state.searchTerm): void => {
        const { searchMode } = this.state;

        const claimTypes: ClaimType[] = (this.props as any)?.rootStore?.metricsStore.selectedClaimTypes ? (this.props as any)?.rootStore?.metricsStore.selectedClaimTypes : [ClaimType.PROFESSIONAL];
        const claimType: ClaimType = (claimTypes.indexOf(ClaimType.INSTITUTIONAL) > -1) ? ClaimType.INSTITUTIONAL : ClaimType.PROFESSIONAL;

        if (!_.isEmpty(searchTerm)) {
            (this.context as any).cancelGroup('search');
            this.setState(
                {
                    searching: true,
                    searchTerm,
                },
                () => {
                    this.debouncedSearch.cancel();
                    this.debouncedSearch(searchMode, searchTerm, claimType);
                },
            );
        }
    };
    private readonly doClose = (): void => {
        this.setState({
            open: false,
            searchTerm: '',
            searchResult: undefined,
            selectedItem: undefined,
        });
    };
    private readonly doSearch = async (mode: ClaimSearchMode, term: string, claimType: ClaimType): Promise<void> => {
        try {
            (this.context as any).setGroup('search');
            const searchResult = await this.injected.rootStore.searchStore.searchForMode(mode, term, claimType);
            const recentSearches = _.takeRight(_.uniq(_.concat(this.state.recentSearches, term)), maxRecentSearches);
            this.setState({
                searchResult,
                searching: false,
                recentSearches,
            });
            ClaimSearchBar.saveRecentSearches(recentSearches);
        } catch (err) {
            await handleError(err);
            this.setState({
                searchResult: undefined,
                searching: false,
            });
        }
    };
    private readonly buildClaimsMap = (searchResult?: ISearchResult): _.Dictionary<string[]> => {
        const map = _.transform(
            _.get(searchResult, 'claims', []),
            (r, claim) => {
                const patientKey = `${claim.patientId}`;
                if (!r[patientKey]) {
                    r[patientKey] = [];
                }
                r[patientKey].push(claim);
            },
            {},
        );
        return _.transform(
            map,
            (out: _.Dictionary<string[]>, claims: ProviderClaim[], key: string) => {
                _.set(
                    out,
                    key,
                    _.orderBy(claims, ['fromDos'], ['desc']).map(claim => claim.id),
                );
            },
            {},
        );
    };
    private readonly selectNextItem = (up: boolean): string => {
        const { searchResult } = this.state;
        const size = _.reduce(
            _.values(searchResult),
            (r, arr) => {
                return arr ? r + arr.length : r;
            },
            0,
        );
        if (size > 0) {
            return this.selectNextResult(up);
        } else {
            return this.selectNextRecent(up);
        }
    };
    private readonly selectNextRecent = (up: boolean): string => {
        return this.selectNext(up, this.state.recentSearches);
    };
    private readonly selectNextResult = (up: boolean): string => {
        const { searchMode, searchResult } = this.state;
        if (searchMode === 'patient' || searchMode === 'guarantor') {
            const claimIds = _.values(this.buildClaimsMap(searchResult));
            return this.selectNext(up, _.flatten(claimIds));
        } else if (searchMode === 'payer') {
            const payerIds = _.compact(_.map(searchResult?.payers, 'id'));
            return this.selectNext(up, payerIds);
        } else if (searchMode === 'provider') {
            const providerIds = _.compact(_.map(searchResult?.providers, 'id'));
            return this.selectNext(up, providerIds);
        }
        return this.selectNext(up, []);
    };
    private readonly selectNext = (up: boolean, items: string[]): string => {
        const currIndex = items.indexOf(this.state.selectedItem as string);
        const nextIndex = up ? (currIndex - 1 + items.length) % items.length : (currIndex + 1) % items.length;
        return items[nextIndex];
    };
    private readonly activateItem = (item: string, newWindow: boolean = false): void => {
        if (this.findClaim(item)) {
            this.activateClaim(item, newWindow);
        } else if (this.findPayer(item)) {
            this.activatePayer(item, newWindow);
        } else if (this.findProvider(item)) {
            this.activateProvider(item, newWindow);
        } else {
            this.activateRecent(item);
        }
    };
    private getAccountId = (): string | undefined => {
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        return pathParts.length > 0 && pathParts[0].length === 36 ? pathParts[0] : undefined;
    };
    private readonly activateClaim = (id?: string, newWindow: boolean = false): void => {
        if (id) {
            this.doClose();
            this.injected.onChange?.();
            const accountId = this.getAccountId();
            const uri = accountId ? `/${accountId}/claims/${id}` : `/claims/${id}`;
            if (newWindow) {
                window.open(uri, '_blank');
            } else {
                window.location.href = uri;
            }
        }
    };
    private readonly activateProvider = (id?: string, newWindow: boolean = false): void => {
        if (id) {
            this.doClose();
            const accountId = this.getAccountId();
            const uri = accountId ? `/${accountId}/claims/by-provider/${id}` : `/claims/by-provider/${id}`;
            if (newWindow) {
                window.open(uri, '_blank');
            } else {
                window.location.href = uri;
            }
        }
    };
    private readonly activatePayer = (id?: string, newWindow: boolean = false): void => {
        if (id) {
            this.doClose();
            const accountId = this.getAccountId();
            const uri = accountId ? `/${accountId}/claims/by-payer/${id}` : `/claims/by-payer/${id}`;
            if (newWindow) {
                window.open(uri, '_blank');
            } else {
                window.location.href = uri;
            }
        }
    };
    private readonly activateRecent = (term: string): void => {
        this.beginSearch(term);
    };
}
